import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../utils.js'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card header content */
  header?: ReactNode
  /** Card footer content */
  footer?: ReactNode
  /** Make the entire card clickable (adds hover state) */
  clickable?: boolean
  /** Padding preset */
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

const paddingClasses = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
}

/**
 * Card container with header, body, and footer slots.
 * Supports clickable variant with hover state.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { header, footer, clickable = false, padding = 'md', className, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-[var(--card-radius)] border-[1.5px] border-[var(--card-border)] bg-[var(--card-bg)]',
        'shadow-[var(--shadow-sm)] transition-all',
        clickable && 'cursor-pointer hover:border-[var(--card-hover-border)] hover:shadow-[var(--card-hover-shadow)] hover:-translate-y-1',
        className,
      )}
      {...props}
    >
      {header && (
        <div className="border-b border-[var(--color-border)] px-4 py-3">
          {header}
        </div>
      )}
      <div className={paddingClasses[padding]}>{children}</div>
      {footer && (
        <div className="border-t border-[var(--color-border)] px-4 py-3">
          {footer}
        </div>
      )}
    </div>
  )
})
